import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/Atoms/Button/Button';
import { ErrorMessage } from '@/components/Atoms/ErrorMessage/ErrorMessage';
import { SkeletonPulse } from '@/components/Atoms/SkeletonPulse/SkeletonPulse';
import { Dialog, useDialog } from '@/components/Molecules/Dialog/Dialog';
import dialogStyles from '@/components/Molecules/Dialog/Dialog.module.css';
import { Stack } from '@/components/Molecules/Stack/Stack';
import type { IPDFViewerHandle } from '@/components/Organisms/PDFViewer/PDFViewer';
import { useExceedsBreakpoint } from '@/hooks/useExceedsBreakpoint';
import useTranslation from '@/hooks/useTranslation';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import styles from './InsightQA.module.css';
import { InsightQAAnswer } from './InsightQAAnswer';
import type { InsightQAState } from './useInsightQA';
import { useInsightQA } from './useInsightQA';
import AiGenerativeIcon from '@bloomberg/web-icons-react/AiGenerative';
import ChevronDoubleDownIcon from '@bloomberg/web-icons-react/ChevronDoubleDown';
import { Badge } from '@/components/Atoms/Badge/Badge';
import classNames from '@/utils/classNames';
export interface IAttribution {
  id: string;
  page: number | null;
  matchedText: string | null;
}
export interface IAnswerElement {
  id: string;
  text: string;
  attributions: IAttribution[];
}
export interface IInsightQAResponse {
  answers: IAnswerElement[];
}
interface IInsightQAProps {
  insightId: string;
  isOpen: boolean;
  onClose: () => void;
  pdfViewerHandle: IPDFViewerHandle;
}
export const InsightQA: FC<IInsightQAProps> = t0 => {
  const $ = _c(24);
  const {
    insightId,
    isOpen,
    onClose,
    pdfViewerHandle
  } = t0;
  const {
    t
  } = useTranslation("pdfViewer");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      threshold: 800
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const exceedsBreakpoint = useExceedsBreakpoint(t1);
  const qaDialog = useDialog();
  const qa = useInsightQA(insightId, isOpen, pdfViewerHandle);
  let t2;
  if ($[1] !== exceedsBreakpoint || $[2] !== isOpen || $[3] !== qaDialog) {
    t2 = () => {
      if (!exceedsBreakpoint && isOpen) {
        qaDialog.open();
      } else {
        qaDialog.close();
      }
    };
    $[1] = exceedsBreakpoint;
    $[2] = isOpen;
    $[3] = qaDialog;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== exceedsBreakpoint || $[6] !== isOpen) {
    t3 = [isOpen, exceedsBreakpoint];
    $[5] = exceedsBreakpoint;
    $[6] = isOpen;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== exceedsBreakpoint || $[9] !== isOpen || $[10] !== onClose || $[11] !== qa || $[12] !== t) {
    t4 = isOpen && exceedsBreakpoint && <aside className={styles.panel}><div className={classNames(dialogStyles.header, styles.header)}><h2 className="cds-t-h4">Ask about this document</h2><Button kind="tertiary" icon={<ChevronDoubleDownIcon />} title={t("common:Actions.close_modal")} onClick={onClose} /></div><InsightQAContent {...qa} /></aside>;
    $[8] = exceedsBreakpoint;
    $[9] = isOpen;
    $[10] = onClose;
    $[11] = qa;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== exceedsBreakpoint || $[15] !== qa) {
    t5 = !exceedsBreakpoint && <InsightQAContent {...qa} />;
    $[14] = exceedsBreakpoint;
    $[15] = qa;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== onClose || $[18] !== qaDialog || $[19] !== t5) {
    t6 = <Dialog {...qaDialog} title="Ask about this document" onClose={onClose}>{t5}</Dialog>;
    $[17] = onClose;
    $[18] = qaDialog;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};
const InsightQAContent: FC<InsightQAState> = t0 => {
  const $ = _c(33);
  const {
    question,
    setQuestion,
    isLoading,
    loadingStartedAt,
    response,
    error,
    handleSubmit,
    handleAttributionClick
  } = t0;
  let t1;
  if ($[0] !== setQuestion) {
    t1 = e => setQuestion(e.target.value);
    $[0] = setQuestion;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== handleSubmit) {
    t2 = e_0 => {
      if (e_0.key === "Enter" && !e_0.shiftKey) {
        e_0.preventDefault();
        handleSubmit(e_0);
      }
    };
    $[2] = handleSubmit;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== question || $[5] !== t1 || $[6] !== t2) {
    t3 = <textarea className={styles.question} value={question} onChange={t1} onKeyDown={t2} placeholder="Ask about this document..." autoFocus={true} />;
    $[4] = question;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isLoading) {
    t4 = <button type="submit" disabled={isLoading}>Submit</button>;
    $[8] = isLoading;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isLoading || $[11] !== t3 || $[12] !== t4) {
    t5 = <fieldset disabled={isLoading} className={styles.fieldset}>{t3}{t4}</fieldset>;
    $[10] = isLoading;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleSubmit || $[15] !== t5) {
    t6 = <form onSubmit={handleSubmit}>{t5}</form>;
    $[14] = handleSubmit;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p><small className={styles.disclaimer}><Badge icon={<AiGenerativeIcon />} text="Generated by Bloomberg AI" /><span>This feature uses Generative AI. Double check responses for accuracy and completeness.</span><a href="/help">Learn more</a></small></p>;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== loadingStartedAt) {
    t8 = loadingStartedAt !== null && <InsightQAThinking startedAt={loadingStartedAt} />;
    $[18] = loadingStartedAt;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== error) {
    t9 = error && <ErrorMessage>{error}</ErrorMessage>;
    $[20] = error;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleAttributionClick || $[23] !== isLoading || $[24] !== response) {
    t10 = !isLoading && response?.answers && <Stack as="ul">{response.answers.map(answer => <li key={answer.id}><InsightQAAnswer answer={answer} onAttributionClick={handleAttributionClick} /></li>)}</Stack>;
    $[22] = handleAttributionClick;
    $[23] = isLoading;
    $[24] = response;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t8 || $[28] !== t9) {
    t11 = <div>{t8}{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t6) {
    t12 = <Stack>{t6}{t7}{t11}</Stack>;
    $[30] = t11;
    $[31] = t6;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
};
const InsightQAThinking: FC<{
  startedAt: number;
}> = t0 => {
  const $ = _c(12);
  const {
    startedAt
  } = t0;
  let t1;
  if ($[0] !== startedAt) {
    t1 = () => Math.floor((Date.now() - startedAt) / 1000);
    $[0] = startedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [seconds, setSeconds] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== startedAt) {
    t2 = () => {
      const interval = setInterval(() => {
        setSeconds(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    };
    t3 = [startedAt];
    $[2] = startedAt;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const t4 = seconds === 1 ? "" : "s";
  let t5;
  if ($[5] !== seconds || $[6] !== t4) {
    t5 = <p className={styles.thinkingLabel}>Thinking for {seconds} second{t4}...</p>;
    $[5] = seconds;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SkeletonPulse lines={3} />;
    t7 = <SkeletonPulse lines={2} />;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t5) {
    t8 = <div className={styles.thinking}>{t5}{t6}{t7}</div>;
    $[10] = t5;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};